import React, { useEffect, useState } from "react";
import { Spin, Tag, Empty } from "antd";
import {
  PhoneOutlined,
  ShopOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import axios from "axios";

const ACCENT = "#27b2c4";

const InfoRows = ({ items }) => (
  <>
    {items.map((it, i) => (
      <div
        key={it.label}
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 16,
          padding: "11px 0",
          borderBottom:
            i === items.length - 1 ? "none" : "1px solid #f5f5f5",
        }}
      >
        <span style={{ color: "#8c8c8c", fontSize: 14 }}>{it.label}</span>
        <span
          style={{
            color: "#1f1f1f",
            fontSize: 14,
            fontWeight: 500,
            textAlign: "right",
            wordBreak: "break-word",
          }}
        >
          {it.value ? it.value : "—"}
        </span>
      </div>
    ))}
  </>
);

const Section = ({ icon, title, children }) => (
  <div
    style={{
      border: "1px solid #f0f0f0",
      borderRadius: 16,
      background: "#fff",
      padding: "18px 22px",
      marginBottom: 18,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 6,
      }}
    >
      <span style={{ color: ACCENT, fontSize: 16, display: "inline-flex" }}>
        {icon}
      </span>
      <h3
        style={{
          margin: 0,
          fontSize: 15,
          fontWeight: 700,
          color: "#1f1f1f",
          letterSpacing: -0.2,
        }}
      >
        {title}
      </h3>
    </div>
    {children}
  </div>
);

const UserDetailView = () => {
  const [userDetails, setUserDetails] = useState([]);
  const [loading, setLoading] = useState(true); // State for loading spinner

  useEffect(() => {
    document.title = "JOJ | Account Details";
    getUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getUserDetails() {
    const token = localStorage.getItem("token");
    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL + "api/UserAccount/GetUserAccountDetail",
        headers: {
          Authorization: "Bearer " + token,
        },
      };

      axios
        .request(config)
        .then((response) => {
          setUserDetails(response.data);
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
        });
    } catch (err) {
      setLoading(false);
    }
  }

  const hasData = userDetails && userDetails.OwnerName;

  // Build initials for the avatar
  const initialsSource = userDetails.OwnerName || userDetails.FirmName || "";
  const initials =
    initialsSource
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?";

  const businessTypes = [
    userDetails.IsOwnShop && "Own Shop",
    userDetails.IsOnlineSeller && "Online Seller",
    userDetails.IsWholeSeller && "Wholesaler",
  ].filter(Boolean);

  return (
    <div style={{ background: "#fff", minHeight: 200 }}>
      <div
        style={{ maxWidth: 720, margin: "0 auto", padding: "40px 20px 64px" }}
      >
        <div style={{ marginBottom: 24 }}>
          <h1
            style={{
              fontSize: 26,
              fontWeight: 700,
              letterSpacing: -0.5,
              margin: 0,
              color: "#1f1f1f",
            }}
          >
            Account Details
          </h1>
          <p style={{ margin: "6px 0 0", color: "#8c8c8c", fontSize: 14 }}>
            Your registered business and contact information
          </p>
        </div>

        <Spin spinning={loading} tip="Loading account details...">
          <div style={{ minHeight: loading ? 160 : undefined }}>
            {!loading && !hasData ? (
              <Empty
                description="No account details found"
                style={{ padding: "56px 0" }}
              />
            ) : hasData ? (
              <>
                {/* Profile header */}
                <div
                  style={{
                    border: "1px solid #f0f0f0",
                    borderRadius: 16,
                    background: "#fafafa",
                    padding: "22px",
                    marginBottom: 18,
                    display: "flex",
                    alignItems: "center",
                    gap: 18,
                  }}
                >
                  <div
                    style={{
                      flexShrink: 0,
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      background: ACCENT,
                      color: "#fff",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 24,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                    }}
                  >
                    {initials}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h2
                      style={{
                        margin: 0,
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#1f1f1f",
                        lineHeight: 1.2,
                      }}
                    >
                      {userDetails.OwnerName || "—"}
                    </h2>
                    {userDetails.FirmName && (
                      <p
                        style={{
                          margin: "3px 0 0",
                          color: "#595959",
                          fontSize: 14.5,
                        }}
                      >
                        {userDetails.FirmName}
                      </p>
                    )}
                    <div style={{ marginTop: 10 }}>
                      <Tag color={userDetails.IsApproved ? "green" : "orange"}>
                        {userDetails.IsApproved
                          ? "Approved"
                          : "Pending approval"}
                      </Tag>
                      {userDetails.IsActive === false && (
                        <Tag color="red">Inactive</Tag>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact */}
                <Section icon={<PhoneOutlined />} title="Contact Information">
                  <InfoRows
                    items={[
                      { label: "Mobile Number", value: userDetails.MobileNo1 },
                      {
                        label: "Alternate Mobile",
                        value: userDetails.MobileNo2,
                      },
                      { label: "Email", value: userDetails.Email1 },
                      { label: "Alternate Email", value: userDetails.Email2 },
                    ]}
                  />
                </Section>

                {/* Business */}
                <Section icon={<ShopOutlined />} title="Business Details">
                  <InfoRows
                    items={[
                      { label: "Company Name", value: userDetails.FirmName },
                      { label: "GSTIN", value: userDetails.GSTIN },
                    ]}
                  />
                  {businessTypes.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 16,
                        paddingTop: 11,
                      }}
                    >
                      <span style={{ color: "#8c8c8c", fontSize: 14 }}>
                        Business Type
                      </span>
                      <span
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 6,
                          justifyContent: "flex-end",
                        }}
                      >
                        {businessTypes.map((t) => (
                          <Tag
                            key={t}
                            style={{ marginRight: 0, borderRadius: 12 }}
                          >
                            {t}
                          </Tag>
                        ))}
                      </span>
                    </div>
                  )}
                </Section>

                {/* Address */}
                <Section icon={<EnvironmentOutlined />} title="Address">
                  <InfoRows
                    items={[
                      { label: "Address", value: userDetails.Address },
                      { label: "City", value: userDetails.City },
                      { label: "State", value: userDetails.State },
                      { label: "Pincode", value: userDetails.PinCode },
                    ]}
                  />
                </Section>
              </>
            ) : null}
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default UserDetailView;
